import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import IsolationCanvas from '../components/IsolationCanvas';
import IsolationPlanSidebar from '../components/IsolationPlanSidebar';
import CommentInput from '../components/CommentInput';
import CommentPopover from '../components/CommentPopover';
import { commentsApi, documentApi, instrumentApi, isolationApi, pnidApi } from '../api';
import { useAuth } from '../contexts/AuthContext';
import type { CanvasComment, IsolationAction, IsolationEquipmentOption, IsolationPlan, IsolationPnidOption, PnidWizardState } from '../types';
import type { HoverableItem } from '../components/IsolationCanvas';
import {
  getInstrumentPrimaryLabel,
  usesGeneratedIdAsPrimaryLabel,
} from '../utils/instrumentIdentity';

const SIDEBAR_WIDTH = 420;

export default function IsolationPlanningPage() {
  const navigate = useNavigate();
  const { pnidNo: pnidNoParam } = useParams();
  const { user } = useAuth();
  const canvasHostRef = useRef<HTMLDivElement | null>(null);

  const [pnidOptions, setPnidOptions] = useState<IsolationPnidOption[]>([]);
  const [equipmentOptions, setEquipmentOptions] = useState<IsolationEquipmentOption[]>([]);
  const [selectedPnidNo, setSelectedPnidNo] = useState(pnidNoParam || '');
  const [selectedEquipmentNo, setSelectedEquipmentNo] = useState('');
  const [plan, setPlan] = useState<IsolationPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingPnids, setLoadingPnids] = useState(false);
  const [loadingEquipments, setLoadingEquipments] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedActionId, setSelectedActionId] = useState<string | null>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  // Comment state
  const [comments, setComments] = useState<CanvasComment[]>([]);
  const [commentMode, setCommentMode] = useState(false);
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(null);
  const [selectedCommentScreenPos, setSelectedCommentScreenPos] = useState<{ x: number; y: number } | null>(null);
  const [showResolvedComments] = useState(false);
  const [commentInputPosition, setCommentInputPosition] = useState<{
    x: number;
    y: number;
    canvasX: number;
    canvasY: number;
  } | null>(null);

  const [pnidData, setPnidData] = useState<PnidWizardState | null>(null);
  const [reviewedInstrumentIds, setReviewedInstrumentIds] = useState<Set<string>>(new Set());
  const [copyUrlMessage, setCopyUrlMessage] = useState<string>('');
  const [reviewConfirmingId, setReviewConfirmingId] = useState<string | null>(null);

  const currentUser = user?.username || 'Anonymous';

  useEffect(() => {
    let isActive = true;
    setLoadingPnids(true);
    setError(null);
    isolationApi.listPnids()
      .then((options) => {
        if (!isActive) {
          return;
        }
        setPnidOptions(options);
        // If pnidNoParam is present in URL but not in state, set it
        if (pnidNoParam && !selectedPnidNo) {
          setSelectedPnidNo(pnidNoParam);
        }
      })
      .catch((err) => {
        if (!isActive) {
          return;
        }
        setError(err instanceof Error ? err.message : 'Failed to load P&ID options.');
      })
      .finally(() => {
        if (isActive) {
          setLoadingPnids(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [pnidNoParam]);

  useEffect(() => {
    if (!selectedPnidNo) {
      setEquipmentOptions([]);
      setSelectedEquipmentNo('');
      setPlan(null);
      return;
    }

    let isActive = true;
    setLoadingEquipments(true);
    setError(null);
    setSelectedEquipmentNo('');
    setPlan(null);

    isolationApi.listEquipments(selectedPnidNo)
      .then((options) => {
        if (!isActive) {
          return;
        }
        setEquipmentOptions(options);
      })
      .catch((err) => {
        if (!isActive) {
          return;
        }
        setEquipmentOptions([]);
        setError(err instanceof Error ? err.message : 'Failed to load equipment options.');
      })
      .finally(() => {
        if (isActive) {
          setLoadingEquipments(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [selectedPnidNo]);

  useEffect(() => {
    const node = canvasHostRef.current;
    if (!node) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      const { width, height } = entry.contentRect;
      setCanvasSize({ width: Math.max(0, Math.floor(width)), height: Math.max(0, Math.floor(height)) });
    });

    observer.observe(node);
    return () => observer.disconnect();
  }, [sidebarOpen]);

  const selectedPnid = useMemo(
    () => pnidOptions.find((option) => option.pnid_no === selectedPnidNo) || null,
    [pnidOptions, selectedPnidNo],
  );

  // Derive reviewed state from server-loaded pnidData so it survives reload/navigation.
  useEffect(() => {
    setReviewConfirmingId(null);
    if (!pnidData) {
      setReviewedInstrumentIds(new Set());
      return;
    }
    setReviewedInstrumentIds(
      new Set(
        (pnidData.instruments ?? [])
          .filter((i) => i.status === 'final_and_reviewed')
          .map((i) => i.id),
      ),
    );
  }, [pnidData]);

  const imageUrl = selectedPnid ? documentApi.getImageUrl(selectedPnid.doc_id) : '';

  const overlayActions = useMemo<IsolationAction[]>(() => {
    if (!plan) {
      return [];
    }
    const merged = [...plan.paths.flatMap((path) => path.actions), ...plan.equipment_actions];
    const deduped = new Map<string, IsolationAction>();
    for (const action of merged) {
      deduped.set(action.instrument_id, action);
    }
    return Array.from(deduped.values()).sort((a, b) => a.step_number - b.step_number);
  }, [plan]);

  // Load comments when the selected P&ID changes
  useEffect(() => {
    if (!selectedPnid) {
      setComments([]);
      return;
    }
    let isActive = true;
    commentsApi.list(selectedPnid.doc_id, true)
      .then((loaded) => {
        if (isActive) setComments(loaded);
      })
      .catch(() => {
        if (isActive) setComments([]);
      });
    return () => { isActive = false; };
  }, [selectedPnid]);

  // Load full P&ID data for hover tooltips
  useEffect(() => {
    if (!selectedPnid) {
      setPnidData(null);
      return;
    }
    let isActive = true;
    pnidApi.get(selectedPnid.doc_id)
      .then((data) => {
        if (isActive) setPnidData(data);
      })
      .catch(() => {
        if (isActive) setPnidData(null);
      });
    return () => { isActive = false; };
  }, [selectedPnid]);

  // Auto-clear review confirmation after 3 seconds
  useEffect(() => {
    if (!reviewConfirmingId) return;
    const timeout = setTimeout(() => {
      setReviewConfirmingId(null);
    }, 3000);
    return () => clearTimeout(timeout);
  }, [reviewConfirmingId]);

  const hoverableItems = useMemo<HoverableItem[]>(() => {
    if (!pnidData) return [];
    const items: HoverableItem[] = [];

    // Build lookup maps for parent references
    const equipmentById = new Map(pnidData.equipments.map((eq) => [eq.id, eq]));
    const nodeById = new Map(pnidData.nodes.map((n) => [n.id, n]));

    for (const eq of pnidData.equipments) {
      if (!eq.bbox) continue;
      items.push({
        id: eq.id,
        category: 'equipment',
        bbox: eq.bbox,
        label: eq.equipment_no,
        properties: { Type: eq.equipment_type, Description: eq.description },
      });
    }

    for (const inst of pnidData.instruments ?? []) {
      if (!inst.bbox) continue;
      const generatedIdLabel = usesGeneratedIdAsPrimaryLabel(inst) ? inst.generated_id ?? undefined : undefined;
      if (inst.context_type === 'equipment' && inst.context_id) {
        const parentEq = equipmentById.get(inst.context_id);
        items.push({
          id: inst.id,
          category: 'equipment_instrument',
          bbox: inst.bbox,
          label: getInstrumentPrimaryLabel(inst),
          properties: {
            Equipment: parentEq?.equipment_no ?? undefined,
            Type: inst.instrument_type ?? undefined,
            'Generated ID': generatedIdLabel,
            Subtype: inst.subtype ?? undefined,
            'Contextual Type': inst.contextual_type ?? undefined,
          },
        });
        continue;
      }

      if (inst.context_type === 'node' && inst.context_id) {
        const node = nodeById.get(inst.context_id);
        items.push({
          id: inst.id,
          category: usesGeneratedIdAsPrimaryLabel(inst) ? 'untagged_instrument' : 'node_instrument',
          bbox: inst.bbox,
          label: getInstrumentPrimaryLabel(inst),
          properties: {
            Line: node?.line_no ?? undefined,
            'Node #': node ? String(node.node_no) : undefined,
            Type: inst.instrument_type ?? undefined,
            'Generated ID': generatedIdLabel,
            Subtype: inst.subtype ?? undefined,
            'Contextual Type': inst.contextual_type ?? undefined,
          },
        });
      }
    }

    for (const node of pnidData.nodes) {
      // Use the actual duct rectangle before falling back to pipeline geometry.
      let bbox = node.is_duct
        ? node.duct_bbox ?? node.label_bbox
        : node.label_bbox;
      if (!bbox && !node.is_duct && node.polyline && node.polyline.length >= 4) {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (let i = 0; i < node.polyline.length; i += 2) {
          const px = node.polyline[i];
          const py = node.polyline[i + 1];
          if (px < minX) minX = px;
          if (px > maxX) maxX = px;
          if (py < minY) minY = py;
          if (py > maxY) maxY = py;
        }
        bbox = { x1: minX, y1: minY, x2: maxX, y2: maxY };
      }
      if (!bbox) continue;
      items.push({
        id: node.id,
        category: 'node',
        bbox,
        label: node.line_no,
        properties: {
          'Node #': String(node.node_no),
          Source: node.source_ref,
          Dest: node.dest_ref,
        },
        polyline: node.is_duct ? undefined : node.polyline ?? undefined,
      });
    }

    return items;
  }, [pnidData]);

  const canGenerate = Boolean(selectedPnidNo && selectedEquipmentNo && !isGenerating);

  // When user selects a PNID, update the URL
  const handleSelectPnid = (pnidNo: string) => {
    setSelectedPnidNo(pnidNo);
    if (pnidNo) {
      navigate(`/isolation/${pnidNo}`);
    } else {
      navigate('/isolation');
    }
  };

  const handleGeneratePlan = async () => {
    if (!canGenerate) {
      return;
    }
    setError(null);
    setIsGenerating(true);
    try {
      const result = await isolationApi.generatePlan(selectedPnidNo, selectedEquipmentNo);
      setPlan(result);
      setSelectedActionId(null);
      setSidebarOpen(true);
    } catch (err) {
      setPlan(null);
      setError(err instanceof Error ? err.message : 'Failed to generate isolation plan.');
    } finally {
      setIsGenerating(false);
    }
  };

  // ---- Comment handlers ----

  const handleCreateComment = useCallback(
    (canvasX: number, canvasY: number, screenX: number, screenY: number) => {
      setCommentInputPosition({ x: screenX, y: screenY, canvasX, canvasY });
    },
    [],
  );

  const handleSubmitComment = useCallback(
    async (text: string) => {
      if (!selectedPnid || !commentInputPosition) return;
      try {
        const newComment = await commentsApi.create(selectedPnid.doc_id, {
          x: commentInputPosition.canvasX,
          y: commentInputPosition.canvasY,
          text,
          author: currentUser,
        });
        setComments((prev) => [...prev, newComment]);
      } catch {
        // silently fail – could show toast in the future
      }
      setCommentInputPosition(null);
    },
    [selectedPnid, commentInputPosition, currentUser],
  );

  const handleSelectComment = useCallback(
    (commentId: string, screenX: number, screenY: number) => {
      setSelectedCommentId(commentId);
      setSelectedCommentScreenPos({ x: screenX, y: screenY });
    },
    [],
  );

  const handleResolveComment = useCallback(async () => {
    if (!selectedPnid || !selectedCommentId) return;
    try {
      const updated = await commentsApi.resolve(selectedPnid.doc_id, selectedCommentId, currentUser);
      setComments((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    } catch { /* ignore */ }
  }, [selectedPnid, selectedCommentId, currentUser]);

  const handleUnresolveComment = useCallback(async () => {
    if (!selectedPnid || !selectedCommentId) return;
    try {
      const updated = await commentsApi.unresolve(selectedPnid.doc_id, selectedCommentId);
      setComments((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    } catch { /* ignore */ }
  }, [selectedPnid, selectedCommentId]);

  const handleDeleteComment = useCallback(async () => {
    if (!selectedPnid || !selectedCommentId) return;
    try {
      await commentsApi.delete(selectedPnid.doc_id, selectedCommentId);
      setComments((prev) => prev.filter((c) => c.id !== selectedCommentId));
      setSelectedCommentId(null);
      setSelectedCommentScreenPos(null);
    } catch { /* ignore */ }
  }, [selectedPnid, selectedCommentId]);

  const handleToggleCommentMode = useCallback(() => {
    setCommentMode((prev) => !prev);
    setCommentInputPosition(null);
    setSelectedCommentId(null);
    setSelectedCommentScreenPos(null);
  }, []);

  // Copy current PNID URL to clipboard
  const handleCopyPnidUrl = useCallback(() => {
    if (!selectedPnid) return;
    const url = `${window.location.origin}/isolation/${selectedPnid.pnid_no}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopyUrlMessage('URL copied!');
      setTimeout(() => setCopyUrlMessage(''), 2000);
    }).catch(() => {
      setCopyUrlMessage('Failed to copy');
      setTimeout(() => setCopyUrlMessage(''), 2000);
    });
  }, [selectedPnid]);

  // Clear confirmation state when selecting a new action
  const handleSelectAction = useCallback((actionId: string) => {
    setSelectedActionId(actionId);
    setReviewConfirmingId(null);  // Clear any pending confirmation
  }, []);

  // Mark instrument as reviewed (requires 2 clicks for confirmation); persists via PATCH.
  const handleMarkAsReviewed = useCallback(async () => {
    if (!selectedActionId || !selectedPnid) return;

    if (reviewConfirmingId !== selectedActionId) {
      setReviewConfirmingId(selectedActionId);
      return;
    }

    const isCurrentlyReviewed = reviewedInstrumentIds.has(selectedActionId);
    const nextStatus = isCurrentlyReviewed ? 'final' : 'final_and_reviewed';

    try {
      await instrumentApi.updateInstrument(selectedPnid.doc_id, selectedActionId, {
        status: nextStatus,
      });
      // Refetch so backend's compute_status × merge_computed_marking_status wins,
      // and the derive-from-pnidData effect repopulates reviewedInstrumentIds.
      const refreshed = await pnidApi.get(selectedPnid.doc_id);
      setPnidData(refreshed);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update review status.');
    } finally {
      setReviewConfirmingId(null);
    }
  }, [selectedActionId, selectedPnid, reviewConfirmingId, reviewedInstrumentIds]);

  return (
    <div style={pageStyle}>
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={() => navigate('/')} style={backBtnStyle} title="Back to app">
            &#8592;
          </button>
          <h1 style={titleStyle}>Isolation Planning</h1>
          {selectedPnid && (
            <div style={pnidRefStyle}>
              <span style={{ fontSize: 12, color: '#6b7280' }}>Reference:</span>
              <span style={{ fontSize: 12, fontWeight: 500, color: '#111827', fontFamily: 'monospace' }}>{selectedPnid.pnid_no}</span>
              <button
                onClick={handleCopyPnidUrl}
                style={copyBtnStyle}
                title="Copy PNID URL to clipboard"
              >
                📋
              </button>
              {copyUrlMessage && (
                <span style={{ fontSize: 11, color: '#059669' }}>{copyUrlMessage}</span>
              )}
            </div>
          )}
        </div>
        {selectedPnid && (
          <button
            onClick={handleToggleCommentMode}
            style={{
              ...commentToggleStyle,
              backgroundColor: commentMode ? '#f59e0b' : '#fff',
              color: commentMode ? '#fff' : '#374151',
              borderColor: commentMode ? '#d97706' : '#d1d5db',
            }}
            title={commentMode ? 'Exit comment mode' : 'Add comments'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            {commentMode ? 'Exit Comments' : 'Comment'}
          </button>
        )}
      </div>

      <div style={controlsStyle}>
        <label style={labelStyle}>
          P&amp;ID
          <select
            value={selectedPnidNo}
            onChange={(e) => handleSelectPnid(e.target.value)}
            style={selectStyle}
            disabled={loadingPnids || isGenerating}
          >
            <option value="">Select P&amp;ID</option>
            {pnidOptions.map((option) => (
              <option key={`${option.doc_id}-${option.pnid_no}`} value={option.pnid_no}>
                {option.pnid_no}
              </option>
            ))}
          </select>
        </label>

        <label style={labelStyle}>
          Equipment
          <select
            value={selectedEquipmentNo}
            onChange={(e) => setSelectedEquipmentNo(e.target.value)}
            style={selectStyle}
            disabled={!selectedPnidNo || loadingEquipments || isGenerating}
          >
            <option value="">Select equipment</option>
            {equipmentOptions.map((option) => (
              <option key={option.equipment_no} value={option.equipment_no}>
                {option.equipment_no}
                {option.description ? ` - ${option.description}` : ''}
              </option>
            ))}
          </select>
        </label>

        <button
          onClick={handleGeneratePlan}
          disabled={!canGenerate}
          style={{
            ...generateBtnStyle,
            opacity: canGenerate ? 1 : 0.6,
            cursor: canGenerate ? 'pointer' : 'not-allowed',
          }}
        >
          {isGenerating ? 'Generating...' : 'Generate Isolation Plan'}
        </button>
      </div>

      {error && <div style={errorStyle}>{error}</div>}

      <div style={contentWrapStyle}>
        <div
          ref={canvasHostRef}
          style={{
            ...canvasPaneStyle,
            right: sidebarOpen ? SIDEBAR_WIDTH : 0,
          }}
        >
          {!selectedPnid && (
            <div style={emptyStyle}>Select a P&amp;ID to load the diagram.</div>
          )}
          {selectedPnid && (
            <IsolationCanvas
              imageUrl={imageUrl}
              actions={overlayActions}
              equipmentIsolations={plan?.equipment_isolations}
              selectedActionId={selectedActionId}
              width={canvasSize.width}
              height={canvasSize.height}
              hoverableItems={hoverableItems}
              comments={comments}
              commentMode={commentMode}
              selectedCommentId={selectedCommentId}
              showResolvedComments={showResolvedComments}
              onCreateComment={handleCreateComment}
              onSelectComment={handleSelectComment}
              reviewedInstrumentIds={reviewedInstrumentIds}
            />
          )}
        </div>

        <IsolationPlanSidebar
          plan={plan}
          actions={overlayActions}
          selectedActionId={selectedActionId}
          onSelectAction={handleSelectAction}
          isOpen={sidebarOpen}
          onToggle={setSidebarOpen}
          width={SIDEBAR_WIDTH}
          reviewedInstrumentIds={reviewedInstrumentIds}
          reviewConfirmingId={reviewConfirmingId}
          onMarkAsReviewed={handleMarkAsReviewed}
        />
      </div>

      {/* Comment Input Dialog */}
      {commentInputPosition && (
        <CommentInput
          position={{ x: commentInputPosition.x, y: commentInputPosition.y }}
          onSubmit={handleSubmitComment}
          onCancel={() => setCommentInputPosition(null)}
        />
      )}

      {/* Comment Popover */}
      {selectedCommentId && selectedCommentScreenPos && (() => {
        const comment = comments.find((c) => c.id === selectedCommentId);
        if (!comment) return null;
        return (
          <CommentPopover
            comment={comment}
            position={selectedCommentScreenPos}
            currentUser={currentUser}
            onResolve={handleResolveComment}
            onUnresolve={handleUnresolveComment}
            onDelete={handleDeleteComment}
            onClose={() => {
              setSelectedCommentId(null);
              setSelectedCommentScreenPos(null);
            }}
          />
        );
      })()}
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#f3f4f6',
  padding: 12,
  gap: 10,
  overflow: 'hidden',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 22,
  color: '#111827',
};

const backBtnStyle: React.CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 8,
  border: '1px solid #d1d5db',
  backgroundColor: '#fff',
  cursor: 'pointer',
};

const pnidRefStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginLeft: 16,
  padding: '6px 12px',
  backgroundColor: '#f0f9ff',
  border: '1px solid #bfdbfe',
  borderRadius: 6,
};

const copyBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: 14,
  padding: 0,
  color: '#6b7280',
  transition: 'color 0.2s',
};

const commentToggleStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  height: 32,
  borderRadius: 8,
  border: '1px solid #d1d5db',
  padding: '0 10px',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  transition: 'all 0.15s ease',
};

const controlsStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-end',
  gap: 12,
  flexWrap: 'wrap',
  backgroundColor: '#ffffff',
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  padding: '10px 14px',
  flexShrink: 0,
};

const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 12,
  fontWeight: 500,
  color: '#374151',
  flex: '1 1 220px',
  maxWidth: 320,
};

const selectStyle: React.CSSProperties = {
  height: 36,
  borderRadius: 8,
  border: '1px solid #d1d5db',
  padding: '0 10px',
  backgroundColor: '#ffffff',
  color: '#111827',
  fontSize: 13,
  width: '100%',
};

const generateBtnStyle: React.CSSProperties = {
  height: 36,
  borderRadius: 8,
  border: '1px solid #2563eb',
  backgroundColor: '#2563eb',
  color: '#ffffff',
  padding: '0 16px',
  fontSize: 13,
  fontWeight: 600,
  flexShrink: 0,
  whiteSpace: 'nowrap',
};

const errorStyle: React.CSSProperties = {
  border: '1px solid #fca5a5',
  backgroundColor: '#fef2f2',
  color: '#b91c1c',
  borderRadius: 8,
  padding: '8px 10px',
  fontSize: 13,
  flexShrink: 0,
};

const contentWrapStyle: React.CSSProperties = {
  position: 'relative',
  flex: '1 1 0%',
  minHeight: 0,
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  overflow: 'hidden',
  backgroundColor: '#fff',
};

const canvasPaneStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  bottom: 0,
  overflow: 'hidden',
  transition: 'right 0.2s ease',
};

const emptyStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#6b7280',
  fontSize: 14,
};
